/* Ali Reviews supplies the counts in the collection's existing ItemList.
 * Gather every filtered page before sorting, then keep Flow's page size,
 * product cards, filters and pagination. Never sort just the visible page.
 */
(() => {
  if (window.SWECollectionReviewSort) return;
  const REVIEW_SORT = 'most-reviews';
  const cache = new Map();
  let requestId = 0;
  let pending;

  function parameters(value) {
    const params = new URLSearchParams(value);
    params.delete('sort_by_desktop');
    params.delete('sort_by_mobile');
    return params;
  }

  function readPage(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const grid = doc.querySelector('#CollectionProductGrid');
    const metadata = grid?.querySelector('[data-swe-review-grid]');
    if (!metadata) throw new Error('Missing collection grid.');
    const schema = [...grid.querySelectorAll('script[type="application/ld+json"]')]
      .map(element => ({element, value: JSON.parse(element.textContent)}))
      .find(({value}) => value['@graph']?.some(node => node['@type'] === 'ItemList'));
    if (!schema) throw new Error('Missing collection review data.');
    const list = schema.value['@graph'].find(node => node['@type'] === 'ItemList');
    const entries = new Map(list.itemListElement.map(entry => [decodeURIComponent(new URL(entry.item.url).pathname.split('/').pop()), entry]));
    const products = [...grid.querySelectorAll('[data-swe-product-handle]')].map(card => {
      const handle = card.dataset.sweProductHandle;
      const entry = entries.get(handle);
      if (!entry) throw new Error('Product review data does not match its card.');
      const count = Number(entry.item.aggregateRating?.reviewCount || 0);
      if (!Number.isInteger(count) || count < 0) throw new Error('Invalid review count.');
      return {handle, count, title: entry.item.name, card, entry, tier: card.dataset.sweDisciplineTier};
    });
    return {
      doc, grid, metadata, schema, list, products, discipline: metadata.dataset.sweDiscipline,
      page: Number(metadata.dataset.page),
      pages: Number(metadata.dataset.pages),
      pageSize: Number(metadata.dataset.pageSize),
      total: Number(metadata.dataset.total),
    };
  }

  function compareProducts(a, b) {
    return b.count - a.count || a.title.localeCompare(b.title) || a.handle.localeCompare(b.handle);
  }

  async function fetchSection(url, signal) {
    const cached = cache.get(url.href);
    if (cached && Date.now() - cached.time < 60000) return cached.html;
    const response = await fetch(url.href, {signal});
    if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);
    const html = await response.text();
    cache.set(url.href, {html, time: Date.now()});
    if (cache.size > 30) cache.delete(cache.keys().next().value);
    return html;
  }

  function sectionURL(params, section, page) {
    const url = new URL(window.location.href);
    url.search = params.toString();
    url.hash = '';
    url.searchParams.set('section_id', section);
    if (page) url.searchParams.set('page', page);
    return url;
  }

  function statusElement(grid) {
    let status = document.getElementById('swe-collection-sort-status');
    if (!status) {
      status = document.createElement('p');
      status.id = 'swe-collection-sort-status';
      status.setAttribute('role', 'status');
      grid.before(status);
    }
    return status;
  }

  function synchronizeSort(controller, value) {
    for (const select of controller.querySelectorAll('select[name^="sort_by_"]')) select.value = value;
    if (controller.sortByInput) controller.sortByInput.value = value;
  }

  async function render(controller, searchParams, event, updateURL = true, initialHTML = null) {
    const id = ++requestId;
    pending?.abort();
    pending = new AbortController();
    const {signal} = pending;
    const params = parameters(searchParams);
    const sort = params.get('sort_by') || REVIEW_SORT;
    const reviewSort = sort === REVIEW_SORT;
    const grid = document.getElementById(controller.productGridId);
    const status = statusElement(grid);
    status.hidden = false;
    status.textContent = reviewSort ? 'Sorting by number of reviews...' : 'Loading products...';
    grid.setAttribute('aria-busy', 'true');
    grid.style.opacity = '0.5';
    if (updateURL) controller.updateURLHash(params.toString());

    try {
      const sourceParams = new URLSearchParams(params);
      if (reviewSort) {
        // Reuse initial HTML and its native order. Later pages must use that
        // same order until the entire result set has been gathered.
        sourceParams.set('sort_by', initialHTML
          ? grid.querySelector('[data-swe-review-grid]').dataset.defaultSort
          : 'title-ascending');
      }
      const section = grid.dataset.sectionId;
      const firstURL = sectionURL(sourceParams, section);
      const firstHTML = initialHTML || await fetchSection(firstURL, signal);
      const result = readPage(firstHTML);

      if (reviewSort || result.discipline) {
        const pages = new Map([[result.page, result]]);
        const remaining = Array.from({length: result.pages}, (_, index) => index + 1).filter(page => page !== result.page);
        // Bound parallel requests even if the catalog grows beyond 250 items.
        await Promise.all(Array.from({length: Math.min(3, remaining.length)}, async () => {
          while (remaining.length) {
            const page = remaining.shift();
            const html = await fetchSection(sectionURL(sourceParams, section, page), signal);
            const parsed = readPage(html);
            if (parsed.page !== page || parsed.total !== result.total || parsed.discipline !== result.discipline) throw new Error('Collection changed during sorting.');
            pages.set(page, parsed);
          }
        }));
        const products = [...pages.entries()].sort(([a], [b]) => a - b).flatMap(([, page]) => page.products);
        if (result.discipline && products.some(product => !['primary', 'secondary'].includes(product.tier))) {
          throw new Error('Missing discipline membership.');
        }
        products.sort((a, b) => {
          const tierOrder = result.discipline ? Number(a.tier === 'secondary') - Number(b.tier === 'secondary') : 0;
          return tierOrder || (reviewSort ? compareProducts(a, b) : 0);
        });
        if (products.length !== result.total || new Set(products.map(product => product.handle)).size !== result.total) {
          throw new Error('Incomplete collection results.');
        }
        const offset = (result.page - 1) * result.pageSize;
        const visible = products.slice(offset, offset + result.pageSize);
        const productList = result.grid.querySelector('#template--collection');
        if (result.total > 0) {
          const nodes = [];
          let previousTier;
          for (const product of visible) {
            if (result.discipline && product.tier !== previousTier) {
              const heading = result.doc.createElement('h2');
              heading.className = `swe-discipline-group-heading swe-discipline-group-heading--${product.tier}`;
              heading.dataset.sweGroupHeading = product.tier;
              heading.textContent = product.tier === 'primary' ? 'Most popular' : 'People also choose';
              nodes.push(heading);
              previousTier = product.tier;
            }
            nodes.push(product.card);
          }
          productList.replaceChildren(...nodes);
        }
        result.list.numberOfItems = visible.length;
        result.list.itemListElement = visible.map((product, index) => ({...product.entry, position: offset + index + 1}));
        result.schema.element.textContent = JSON.stringify(result.schema.value).replace(/</g, '\\u003c');
        for (const link of result.grid.querySelectorAll('.pagination a')) {
          const url = new URL(link.getAttribute('href'), window.location.href);
          url.searchParams.set('sort_by', sort);
          url.searchParams.delete('section_id');
          link.setAttribute('href', url.pathname + url.search);
        }
        // Native filter-removal links carry the fetch order; keep the shopper's
        // selected order when removing a Color or Size filter.
        for (const link of result.doc.querySelectorAll('.js-facet-remove')) {
          const url = new URL(link.getAttribute('href'), window.location.href);
          url.searchParams.set('sort_by', sort);
          url.searchParams.delete('section_id');
          link.setAttribute('href', url.pathname + url.search);
        }
      }

      if (id !== requestId) return;
      const html = result.doc.documentElement.outerHTML;
      controller.renderFilters(html, event);
      synchronizeSort(controller, sort);
      controller.renderProductGrid(html);
      status.textContent = '';
      status.hidden = true;
      window.eventBus?.emit('collection:filters:change', {details: {parameters: params.toString()}});
      if (event?.type === 'click' && event.target?.closest('.pagination')) grid.scrollIntoView({block: 'start'});
    } catch (error) {
      if (id !== requestId || error.name === 'AbortError') return;
      // Keep the existing usable grid and make failure explicit. Never label
      // an incompletely fetched collection as successfully sorted.
      status.hidden = false;
      status.textContent = 'Could not load the requested order. Previous products are still shown. ';
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.textContent = 'Retry';
      retry.addEventListener('click', () => render(controller, searchParams, null, false));
      status.append(retry);
      console.warn('Collection sorting failed.', error);
    } finally {
      if (id === requestId) {
        grid.removeAttribute('aria-busy');
        grid.style.removeProperty('opacity');
      }
    }
  }

  window.SWECollectionReviewSort = {render};
  customElements.whenDefined('collection-filtering-form').then(() => {
    const controller = document.querySelector('collection-filtering-form');
    const grid = document.getElementById('CollectionProductGrid');
    if (!controller || !grid) return;
    const sort = new URLSearchParams(window.location.search).get('sort_by');
    if (!sort || sort === REVIEW_SORT || grid.querySelector('[data-swe-discipline]')) render(controller, window.location.search, null, false, document.documentElement.outerHTML);

    document.addEventListener('click', event => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('#CollectionProductGrid .pagination a');
      if (!link) return;
      const url = new URL(link.href);
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;
      if (url.searchParams.get('sort_by') !== REVIEW_SORT && !grid.querySelector('[data-swe-discipline]')) return;
      event.preventDefault();
      controller.renderPage(url.searchParams.toString(), event);
    }, true);
  });
})();
